import React, { ComponentProps, ReactNode } from 'react';

import PropTypes from 'prop-types';

import Button from '@adsk/alloy-react-button';

import Banner from './Banner';

const Levels = {
  INFO: 'info',
  WARNING: 'warning',
  ERROR: 'error',
  SUCCESS: 'success',
};

type BannerLevels = typeof Levels;
export type BannerLevel = BannerLevels[keyof BannerLevels];

type Action = {
  className?: string;
  onClick?: () => void;
  text?: ReactNode;
};

type BannerBimAdapterProps = ComponentProps<typeof Banner> & {
  level: BannerLevel;
  actions?: Action[];
  content?: ReactNode;
};

function getVariant(level: BannerLevel) {
  switch (level) {
    case Levels.INFO:
      return Banner.VARIANTS.PRIMARY;
    case Levels.WARNING:
      return Banner.VARIANTS.WARNING;
    case Levels.ERROR:
      return Banner.VARIANTS.ERROR;
    case Levels.SUCCESS:
      return Banner.VARIANTS.SUCCESS;
    default:
      return Banner.VARIANTS.PRIMARY;
  }
}

function makeActions(actions: Action[]) {
  const finalActions = Array.isArray(actions) ? actions : [actions];

  return finalActions.map((action, key) => {
    const { className, onClick, text } = action;

    return (
      <Button
        key={key}
        className={className}
        onClick={onClick}
        variant={Button.VARIANTS.SECONDARY}
        size={Button.SIZES.SMALL}
      >
        {text}
      </Button>
    );
  });
}

const BannerBimAdapter = ({
  children,
  level,
  actions,
  content,
  ...props
}: BannerBimAdapterProps) => (
  <Banner variant={getVariant(level)} message={content} {...props}>
    {actions ? makeActions(actions) : children}
  </Banner>
);

BannerBimAdapter.Levels = Levels;

const actionShape = PropTypes.shape({
  className: PropTypes.string,
  href: PropTypes.string,
  onClick: PropTypes.func,
  target: PropTypes.string,
  text: PropTypes.node.isRequired,
});

BannerBimAdapter.propTypes = {
  ...Banner.propTypes,
  /** level of the banner. Can be one of Variants */
  level: PropTypes.oneOf(Object.values(BannerBimAdapter.Levels)),
  /** The main content to display for the banner */
  content: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /** Custom actions to show next to content using the Button Component */
  actions: PropTypes.oneOfType([PropTypes.arrayOf(actionShape), actionShape]),
  /** The component's children, use this when `actions` is empty */
  children: PropTypes.node,
};

export default BannerBimAdapter;
